/* eslint-disable camelcase */
import { FC, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { platform } from 'network-utils';
import { useSelector, useDispatch } from 'react-redux';
import { translate } from 'translations';
import { EmailNotifications, Divider } from 'learning-panel-components';
import { selectUserUuid } from '@redux/slices/userSlice';
import { showAlert } from '@redux/slices/informationSlice';
import { useGetAccountData, getAccountData } from '@queries/platform/accountSettings';

const EmailNotificationsContainer: FC<EmailNotificationsContainerProps> = (props: EmailNotificationsContainerProps) => {
    const { setShowSuccessNotification, setNotificationMessage } = props;
    const services = platform.learningPanel.AccountSettingsAPIs;
    const [isNotificationsEnabled, setIsNotificationsEnabled] = useState<boolean>(false);
    const [subscribeToNotificationsEmails, setSubscribeToNotificationsEmails] = useState<boolean>(true);
    const [subscribeToNotificationsEmailsIsLoading, setSubscribeToNotificationsEmailsIsLoading] =
        useState<boolean>(false);
    const [subscribeToProgressEmails, setSubscribeToProgressEmails] = useState<boolean>(true);
    const [subscribeToProgressEmailsIsLoading, setSubscribeToProgressEmailsIsLoading] = useState<boolean>(false);
    const userUuid = useSelector(selectUserUuid);
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    const generalError = translate('Something went wrong, please try again later');
    const emailNotificationsUpdated = translate('emailNotificationsUpdated');
    const monthlyProgressReminderUpdated = translate('monthlyProgressReminderUpdated');

    const { data } = useGetAccountData(userUuid);
    const accountData = data?.account_data;

    useEffect(() => {
        if (accountData) {
            setIsNotificationsEnabled(accountData.email_notifications_enabled);
            setSubscribeToNotificationsEmails(accountData?.subscribe_to_notification_emails || false);
            setSubscribeToProgressEmails(accountData?.subscribe_to_progress_emails || false);
        }
    }, [accountData]);

    const showError = (error: string) => {
        dispatch(
            showAlert({
                value: error,
                status: 'error'
            })
        );
    };

    const updateEmailNotificationsSubscribe = async (input: {
        subscribe_to_all_email_notifications: boolean;
    }): Promise<{ success: boolean; error?: string }> => {
        setSubscribeToNotificationsEmails(input.subscribe_to_all_email_notifications);
        setSubscribeToNotificationsEmailsIsLoading(true);
        const result = await services.updateEmailNotificationsSubscribe(input);
        return result;
    };

    const updateProgressEmailNotificationsSubscribe = async (input: {
        subscribe_to_email_progress_notifications: boolean;
    }): Promise<{ success: boolean; error?: string }> => {
        setSubscribeToProgressEmails(input.subscribe_to_email_progress_notifications);
        setSubscribeToProgressEmailsIsLoading(true);
        const result = await services.updateProgressEmailNotificationsSubscribe(input);
        return result;
    };

    const { mutate: mutateEmailNotifications } = useMutation(updateEmailNotificationsSubscribe, {
        onSuccess: (result: { success: boolean; error?: string }, variables) => {
            if (result.success) {
                queryClient.setQueryData([getAccountData, userUuid], oldData => ({
                    account_data: {
                        ...(oldData as platform.AccountData).account_data,
                        subscribe_to_notification_emails: variables.subscribe_to_all_email_notifications,
                        ...(!variables.subscribe_to_all_email_notifications && { subscribe_to_progress_emails: false })
                    }
                }));
                setSubscribeToNotificationsEmailsIsLoading(false);
                setShowSuccessNotification?.(true);
                setNotificationMessage?.(emailNotificationsUpdated);
                return;
            }
            setSubscribeToNotificationsEmails(prev => !prev);
            setSubscribeToNotificationsEmailsIsLoading(false);
            showError(result?.error || generalError);
        },
        onError: () => {
            setSubscribeToNotificationsEmails(prev => !prev);
            setSubscribeToNotificationsEmailsIsLoading(false);
            showError(generalError);
        }
    });

    const { mutate: mutateProgressEmailNotificationProgress } = useMutation(updateProgressEmailNotificationsSubscribe, {
        onSuccess: (result: { success: boolean; error?: string }, variables) => {
            if (result.success) {
                queryClient.setQueryData([getAccountData, userUuid], oldData => ({
                    account_data: {
                        ...(oldData as platform.AccountData).account_data,
                        subscribe_to_progress_emails: variables.subscribe_to_email_progress_notifications
                    }
                }));
                setSubscribeToProgressEmailsIsLoading(false);
                setShowSuccessNotification?.(true);
                setNotificationMessage?.(monthlyProgressReminderUpdated);
                return;
            }
            setSubscribeToProgressEmails(prev => !prev);
            setSubscribeToProgressEmailsIsLoading(false);
            showError(result?.error || generalError);
        },
        onError: () => {
            setSubscribeToProgressEmails(prev => !prev);
            setSubscribeToProgressEmailsIsLoading(false);
            showError(generalError);
        }
    });

    return isNotificationsEnabled ? (
        <>
            <EmailNotifications
                subscribeToNotificationsEmails={subscribeToNotificationsEmails}
                subscribeToProgressEmails={subscribeToProgressEmails}
                setSubscribeToNotificationsEmails={(value: boolean) =>
                    mutateEmailNotifications({
                        subscribe_to_all_email_notifications: value
                    })
                }
                setSubscribeToProgressEmails={(value: boolean) =>
                    mutateProgressEmailNotificationProgress({
                        subscribe_to_email_progress_notifications: value
                    })
                }
                isSubscribeToNotificationsEmailsDisabled={subscribeToNotificationsEmailsIsLoading}
                isSubscribeToProgressEmailsDisabled={subscribeToProgressEmailsIsLoading}
            />
            <Divider color="rgba(255, 255, 255, 0.25)" />
        </>
    ) : null;
};
export interface EmailNotificationsContainerProps {
    setShowSuccessNotification?: Function;
    setNotificationMessage?: Function;
}

export default EmailNotificationsContainer;
